import React, { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../../services/api';
import type { Media } from '../../types/api';
import { convertToWebP, formatBytes } from '../../utils/imageOptimizer';

export const AdminMediaPage: React.FC = () => {
    const { setFlashMessage } = useOutletContext<{ setFlashMessage: (msg: any) => void }>();
    const [mediaList, setMediaList] = useState<Media[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [uploading, setUploading] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const loadMedia = async () => {
        setLoading(true);
        try {
            const res = await api.adminGetMedia(page);
            setMediaList(res.data);
            if (res.meta) {
                setTotalPages(res.meta.last_page);
            }
        } catch {
            // error handled
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMedia();
    }, [page]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            // 1. Automatically convert image to modern WebP format
            const webp = await convertToWebP(file, { maxWidth: 1920, quality: 0.85 });

            // 2. Upload optimized WebP payload
            await api.adminUploadMedia({
                filename: webp.filename,
                original_name: webp.originalName,
                data: webp.dataUrl,
                width: webp.width,
                height: webp.height,
                size_bytes: webp.sizeBytes,
                alt_text: webp.originalName.replace(/\.[^/.]+$/, ''),
            });

            setFlashMessage({
                type: 'success',
                text: `Image converted to WebP successfully! (${formatBytes(webp.originalSizeBytes)} → ${formatBytes(webp.sizeBytes)}, saved ${webp.savingsPercent}%)`
            });
            loadMedia();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to upload media.' });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete media file permanently?')) return;
        try {
            await api.adminDeleteMedia(id);
            setFlashMessage({ type: 'success', text: 'Media asset deleted.' });
            loadMedia();
        } catch (err: any) {
            setFlashMessage({ type: 'error', text: err.message || 'Failed to delete media.' });
        }
    };

    const copyUrl = (path: string) => {
        const fullUrl = `${window.location.origin}/storage/${path}`;
        navigator.clipboard.writeText(fullUrl);
        setFlashMessage({ type: 'success', text: 'Media URL copied to clipboard!' });
    };

    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-white tracking-tight">Media Library</h1>
                    <p className="text-sm text-slate-400 mt-1">Automatic WebP compression, dimension clamping, and thumbnail generation.</p>
                </div>

                <div>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileUpload} 
                        accept="image/*" 
                        className="hidden" 
                        id="media-upload-input" 
                    />
                    <label 
                        htmlFor="media-upload-input"
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg transition cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                        {uploading ? 'Processing WebP...' : '+ Upload Image'}
                    </label>
                </div>
            </div>

            {/* Media Grid */}
            <div className="p-6 rounded-3xl glass-card">
                {mediaList.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {mediaList.map(item => {
                            const mediaUrl = `/storage/${item.path}`;
                            return (
                                <div key={item.id} className="relative group rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex flex-col justify-between">
                                    <div className="aspect-square w-full overflow-hidden bg-slate-950 flex items-center justify-center p-2">
                                        <img 
                                            src={mediaUrl} 
                                            alt={item.alt_text || item.original_name} 
                                            className="w-full h-full object-cover rounded-xl group-hover:scale-105 transition"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-3 space-y-1">
                                        <p className="text-xs font-semibold text-white truncate" title={item.original_name}>
                                            {item.original_name}
                                        </p>
                                        <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
                                            <span>{item.width && item.height ? `${item.width}×${item.height}` : 'WebP'}</span>
                                            <span>{Math.round(item.size_bytes / 1024)} KB</span>
                                        </div>
                                    </div>
                                    <div className="p-2 border-t border-slate-800 flex items-center justify-between text-[11px]">
                                        <button 
                                            type="button" 
                                            onClick={() => copyUrl(item.path)}
                                            className="text-emerald-400 hover:text-emerald-300 font-semibold cursor-pointer"
                                        >
                                            Copy URL
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={() => handleDelete(item.id)}
                                            className="text-rose-400 hover:text-rose-300 cursor-pointer"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="py-16 text-center text-slate-500">
                        {loading ? 'Loading media library...' : 'No media assets uploaded yet.'}
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="mt-8 flex items-center justify-end gap-2 text-xs">
                        <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage(page - 1)}
                            className="px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <span className="text-slate-400">Page {page} of {totalPages}</span>
                        <button
                            type="button"
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                            className="px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
